interface AgentCardProps {
  name: string;
  type: string;
  status: "idle" | "active" | "running" | "paused" | "error";
  authorityLevel: number;
  tasksCompleted: number;
  passRate: number;
}

const STATUS_TOKENS = {
  idle:    { color: "var(--color-status-idle)",    label: "Idle",    border: "rgba(120,113,108,0.4)", bg: "rgba(120,113,108,0.08)", pulse: false },
  active:  { color: "var(--color-status-active)",  label: "Active",  border: "rgba(132,204,22,0.4)",  bg: "rgba(132,204,22,0.08)",  pulse: true  },
  running: { color: "var(--color-status-running)", label: "Running", border: "rgba(56,189,248,0.4)",  bg: "rgba(56,189,248,0.08)",  pulse: true  },
  paused:  { color: "var(--color-status-paused)",  label: "Paused",  border: "rgba(245,158,11,0.4)",  bg: "rgba(245,158,11,0.08)",  pulse: false },
  error:   { color: "var(--color-status-error)",   label: "Error",   border: "rgba(239,68,68,0.4)",   bg: "rgba(239,68,68,0.08)",   pulse: true  },
} as const;

function passRateColor(rate: number) {
  if (rate >= 95) return "var(--color-status-active)";
  if (rate >= 80) return "var(--color-accent-bright)";
  if (rate >= 50) return "var(--color-status-paused)";
  return "var(--color-status-error)";
}

export function AgentCard({ name, type, status, authorityLevel, tasksCompleted, passRate }: AgentCardProps) {
  const s = STATUS_TOKENS[status];

  return (
    <div className="panel panel-hover p-5 group">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="relative flex items-center justify-center" style={{ color: s.color }}>
            {s.pulse ? (
              <span className="live-dot" />
            ) : (
              <span className="block w-2 h-2 rounded-full bg-current" />
            )}
          </span>
          <h3 className="font-display text-lg italic leading-tight text-[var(--color-text)] truncate">
            {name}
          </h3>
        </div>
        <span
          className="pill shrink-0"
          style={{ color: s.color, borderColor: s.border, background: s.bg }}
        >
          {s.label}
        </span>
      </div>

      {/* Meta */}
      <div className="flex items-center gap-2 mb-5 font-mono text-[10px] tracking-wider uppercase">
        <span className="px-1.5 py-0.5 rounded border border-[var(--color-border)] text-[var(--color-text-muted)]">
          {type}
        </span>
        <span className="text-[var(--color-text-dim)]">·</span>
        <span className="text-[var(--color-text-muted)]">
          Authority{" "}
          <span className="text-[var(--color-accent)]">L{authorityLevel}</span>
        </span>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-5 pt-4 border-t border-dashed border-[var(--color-border)]">
        <div>
          <p className="eyebrow">Tasks</p>
          <p className="font-display text-2xl italic mt-1 text-[var(--color-text)] tabular-nums">
            {tasksCompleted}
          </p>
        </div>
        <div>
          <p className="eyebrow">Pass Rate</p>
          <p
            className="font-display text-2xl italic mt-1 tabular-nums"
            style={{ color: passRateColor(passRate) }}
          >
            {passRate}
            <span className="text-sm align-top font-mono not-italic ml-0.5">%</span>
          </p>
        </div>
      </div>
    </div>
  );
}
